import React, { useState } from "react";
import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { TLocationHandler } from "../_types/location.type";
import { ValidationInput } from "../validation/components/ValidationInput";
import { FormButton } from "../../../components/login/button.component";
import { postCheckOtp } from "../../../_services/auth.service";
import { authThrottleMessage, isAuthThrottleCode } from "../../../utils/authThrottleMessage";
import { IErrorResponse } from "../../../_services/interface/IDefaults";
import { PasswordResetForm } from "../PasswordReset/PasswordReset";
import { SimpleFormError, SimpleFormValid } from "../../../components/login/formError.component";
import { patchResendMailMail } from "../../../_services/user.service";
import { IResendValidationResponse } from "../../../_services/interface/user.interface";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../../components/ui/lovable/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";

interface VerificationProps {
  locationHandler: TLocationHandler;
  email: string;
}

export const VerificationForm = (props: VerificationProps) => {
  const [counter, setCounter] = React.useState(100);
  const [isResendDisabled, setResendDisabled] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidationBtnDisabled, setValidationBtn] = useState<boolean>(true);
  const [validationCode, setValidationCode] = useState<string>("");
  const [okMessage, setOkMessage] = useState("");

  React.useEffect(() => {
    counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
    if (counter === 0) {
      setResendDisabled(false);
    }
  }, [counter]);

  /**
   * Resend the validation information
   */
  const resendClick = async () => {
    setCounter(100);
    setResendDisabled(true);
    setOkMessage("");
    setErrorMessage("");

    const { status, response } = await patchResendMailMail(props.email);

    if (status === 200) {
      if ((response as IResendValidationResponse).mailsent) {
        // GEN-3039: o servidor tem cooldown de 60s no reenvio — dentro dessa janela ele responde
        // sucesso mas NÃO manda e-mail novo, porque o código anterior ainda serve. A redação
        // cobre os dois casos sem prometer um e-mail que pode não ter saído.
        setOkMessage("Verifique seu e-mail. Se você já recebeu um código há pouco, ele continua válido.");
      } else {
        setErrorMessage("Falha ao reenviar o código.");
      }
    } else if ((response as IErrorResponse)?.message === "AUTH_TOO_MANY_ATTEMPTS") {
      /**
       * GEN-3039: a rota de reenvio passou a ter limite (dispara e-mail).
       *
       * O contador local é fixo em 100s, mas o limite do servidor é de 15 minutos por
       * identidade — reabilitar o botão em 100s fazia a própria tela instruir o usuário a
       * tentar de novo cedo demais, tomar outro 429 e repetir o ciclo a cada 100s até a janela
       * real fechar. Aqui o contador passa a obedecer o `retryAfter` que o servidor mandou, que
       * é o mesmo número já usado na mensagem.
       */
      const retryAfter = (response as IErrorResponse).retryAfter;
      if (retryAfter && retryAfter > 100) {
        setCounter(Math.ceil(retryAfter));
      }
      setErrorMessage(authThrottleMessage(retryAfter, "code"));
    } else {
      setErrorMessage("Falha ao reenviar o código.");
    }
  };

  const handleCode = (code: string) => {
    setValidationCode(code);
    if (code.length === 6) {
      setValidationBtn(false);
    } else {
      setValidationBtn(true);
    }
  };

  const sendValidation = async () => {
    setValidationBtn(true);
    setOkMessage("");
    setErrorMessage("");
    //Here the system dispatch the code
    const { status, response } = await postCheckOtp({
      otp: validationCode,
      email: props.email,
    });

    if (status === 200 && response === true) {
      props.locationHandler(
        <PasswordResetForm locationHandler={props.locationHandler} otp={validationCode} email={props.email} />
      );
    } else if (status === 200) {
      // GEN-3039: codigo errado volta 200 com `false`. Antes o `if (response as boolean)` sem `else`
      // deixava a tela MUDA — o usuario digitava o codigo errado e nada acontecia. Agora a mensagem
      // cobre os tres casos que o backend nao distingue de proposito (errado, expirado, queimado).
      setErrorMessage("Código inválido ou expirado. Se necessário, solicite um novo código.");
    } else if (isAuthThrottleCode((response as IErrorResponse)?.message)) {
      setErrorMessage(authThrottleMessage((response as IErrorResponse)?.retryAfter, "code"));
    } else {
      setErrorMessage("Não foi possível validar o código. Tente novamente.");
    }
    setValidationBtn(false);
  };

  return (
    <Flex fontFamily="Poppins" fontWeight={500} alignItems="center" mt="0px" w={396}>
      <Flex direction="column" w="100%">
        <Text fontWeight={700} fontFamily="Poppins" fontSize="32px" textAlign="center">
          Verificação
        </Text>
        <Box mt={12}>
          <Flex direction="column" w="396px" alignSelf="center">
            <Text fontSize="12px">
              Se existir uma conta para o email informado, foi enviado um código de 6 dígitos. Digite-o abaixo para
              continuar.
            </Text>
          </Flex>
          {errorMessage ? <SimpleFormError msg={errorMessage} /> : null}
          {okMessage ? <SimpleFormValid msg={okMessage} /> : null}
          <form>
            <Flex alignItems="stretch" justifyContent="center" direction="column" mt={12}>
              <Flex direction="column" alignItems="stretch">
                <Flex justifyContent={"center"}>
                  <InputOTP id="digits-only" maxLength={6} pattern={REGEXP_ONLY_DIGITS} onChange={handleCode}>
                    <InputOTPGroup>
                      <InputOTPSlot className="h-16 w-16" index={0} />
                      <InputOTPSlot className="h-16 w-16" index={1} />
                      <InputOTPSlot className="h-16 w-16" index={2} />
                      <InputOTPSlot className="h-16 w-16" index={3} />
                      <InputOTPSlot className="h-16 w-16" index={4} />
                      <InputOTPSlot className="h-16 w-16" index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </Flex>
                <Flex alignItems="stretch" justifyContent="center" direction="column" mt={12} gap={2}>
                  <FormButton disabled={isValidationBtnDisabled} onClick={sendValidation}>
                    Validar
                  </FormButton>
                  <FormButton onClick={resendClick} disabled={isResendDisabled}>
                    Reenviar Código
                  </FormButton>
                  <Flex alignItems="center" justifyContent="center">
                    <Text fontSize="12px" opacity={0.5}>
                      Você pode reenviar um novo código em {counter} segundos
                    </Text>
                  </Flex>
                  <Text fontSize="12px" opacity={0.5} mt={12}>
                    <strong>Precisa de mais ajuda? </strong>Se você tentou resetar a sua conta mas não recebeu o email
                    da Taxly, verifique a lixeira ou a pasta de spam do seu email. Se ainda sim você não consegue
                    receber o seu email, entre em contato com o nosso{" "}
                    <Link textDecoration="none" color="#4B4EFF" href="#">
                      Help Desk
                    </Link>
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </Box>
      </Flex>
    </Flex>
  );
};
